import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, MapPin, Calendar, Clock, Mail } from "lucide-react";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import Logo from "../components/Logo";
import CatalogueRequest from "../components/CatalogueRequest";
import Seo from "../components/Seo";

const POSTER = "/exhibition/looking-glass-poster.jpg";
const ARTIST_PORTRAIT = "/exhibition/grant-de-longueuil-portrait.jpg";

const ARTIST = {
  id: "grant-de-longueuil",
  name: "Rebecca Grant de Longueuil",
  locale: "British Artist",
  image: ARTIST_PORTRAIT,
  paragraphs: [
    "Rebecca Grant de Longueuil is a British artist whose practice brings together abstraction, impressionism and illustration through a visual language defined by theatre, playfulness and drama. Painting functions for her as a process of self-discovery, engaging with questions of humanity, nature, spirituality and realities beyond what is immediately visible.",
    "The origins of Yellow Brick Road date back to Grant de Longueuil's painting 21st Century Britain, which received the Young Artists Award from the then Prince Charles, now King Charles III. The work marked the beginning of the creative and philosophical thread that continues throughout the collection today.",
    "Her visual language combines ideal and magical elements with earthy colour and a constructive technique informed by Cubism and by the work of her grandfather. Her broader creative background also extends to performance, including appearances in West End productions and television.",
  ],
};

const CATALOG = [
  {
    n: "01",
    artist: "Rebecca Grant de Longueuil",
    title: "21st Century Britain",
    titleAlt: "Yellow Brick Road · Collection",
    year: "1998",
    medium: "Acrylic on board",
    dimensions: "27 × 37 × 1 in",
    image: "/exhibition/ybr-21st-century-britain-1998.jpg",
    note: "The painting that began the Yellow Brick Road. Selected by the then Prince Charles, now King Charles III, for the Young Artist Award 1998, presented to Rebecca Grant de Longueuil through The Prince's Foundation and exhibited at Hampton Court Palace.",
  },
  {
    n: "02",
    artist: "Rebecca Grant de Longueuil",
    title: "The Chanteuse",
    titleAlt: "Yellow Brick Road · Collection",
    year: "2024",
    medium: "Acrylic on canvas, gold leaf",
    dimensions: "24 × 36 × 2.5 in",
    image: "/exhibition/ybr-the-chanteuse-2024.jpg",
    note: "",
  },
  {
    n: "03",
    artist: "Rebecca Grant de Longueuil",
    title: "The Pianist",
    titleAlt: "Yellow Brick Road · Collection",
    year: "2024",
    medium: "Acrylic on canvas, gold leaf",
    dimensions: "24 × 36 × 2.5 in",
    image: "/exhibition/ybr-the-pianist-2024.jpg",
    note: "",
  },
  {
    n: "04",
    artist: "Rebecca Grant de Longueuil",
    title: "Finding God",
    titleAlt: "Yellow Brick Road · Collection",
    year: "2026",
    medium: "Acrylic on canvas, silver leaf",
    dimensions: "23½ × 33½ × 2.5 in",
    image: "/exhibition/ybr-finding-god-2026.jpg",
    note: "",
  },
  {
    n: "05",
    artist: "Rebecca Grant de Longueuil",
    title: "The Trumpet",
    titleAlt: "Yellow Brick Road · Limited Collection",
    year: "2015",
    medium: "Acrylic on canvas",
    dimensions: "19½ × 8 × 2.5 in",
    image: "/exhibition/ybr-the-trumpet-2015.jpg",
    note: "",
  },
  {
    n: "06",
    artist: "Rebecca Grant de Longueuil",
    title: "Alex",
    titleAlt: "Yellow Brick Road · Limited Collection",
    year: "2015",
    medium: "Acrylic on canvas",
    dimensions: "14 × 10 × 2.5 in",
    image: "/exhibition/ybr-alex-2015.jpg",
    note: "",
  },
  {
    n: "07",
    artist: "Rebecca Grant de Longueuil",
    title: "Rhythm of Life",
    titleAlt: "Yellow Brick Road · Limited Collection",
    year: "2026",
    medium: "Acrylic on canvas",
    dimensions: "40 × 40 × 2.5 in",
    image: "/exhibition/ybr-rhythm-of-life-2026.jpg",
    note: "",
  },
  {
    n: "08",
    artist: "Rebecca Grant de Longueuil",
    title: "Amy",
    titleAlt: "Yellow Brick Road · Limited Collection",
    year: "2023",
    medium: "Acrylic on canvas, gold leaf",
    dimensions: "24 × 36 × 2.5 in",
    image: "/exhibition/ybr-amy-2023.jpg",
    note: "",
  },
];

const CURATORIAL = [
  "Through the Looking Glass presents Rebecca Grant de Longueuil's Yellow Brick Road, a body of work concerned with the fragile boundary between what is visible and what might exist beyond it: between the physical and the spiritual, reality and imagination, the paths we inherit and those we choose to create for ourselves.",
  "The collection emerges from an experience that profoundly shaped Grant de Longueuil's understanding of life. At sixteen, following a severe asthma attack, she fell into a coma with a fifty-percent chance of survival. What she experienced during that period became a lasting point of reference within her practice, informing an understanding of existence that extends beyond the purely material and a heightened awareness of the value, possibility and impermanence of everyday life.",
  "Yet Yellow Brick Road is not a meditation on mortality. At its centre is an insistence on life. For Grant de Longueuil, the road is not a predetermined route towards an inevitable destination. It is something continuously made and remade through choice. Rejecting the idea of fate as an external force, her paintings propose instead that we participate in constructing our own trajectories: that we are, in her words, \u201cthe creators of our own journey.\u201d",
  "This is where the looking glass becomes significant. It does not simply reflect the world back to us. It marks a threshold between the reality we accept and the reality we might imagine differently.",
  "Across the works, the physical world encounters something more elusive. Figures, symbols, landscapes and fragments of narrative coexist within compositions that move between the recognisable and the fantastical. Earthy materiality sits beside moments of exuberance and visual instability, while Grant de Longueuil's constructive, cubist-inflected language allows multiple perspectives to occupy the same surface. The paintings resist a singular reality; they appear instead as spaces in which different states of being can overlap.",
  "The artist returns repeatedly to the distractions, temptations and structures that can prevent us from questioning the lives we have come to accept. The status quo is not presented as something that must simply be rejected, but something that should be encountered consciously. To follow a path has meaning only when we recognise that another path could have been chosen.",
  "Running through this is the figure of the daydreamer. Grant de Longueuil describes daydreaming as a precious and increasingly rare activity \u2014 one gradually conditioned out of us as we move towards adulthood. Within Yellow Brick Road, imagination therefore becomes more than escapism. It becomes a form of agency: the ability to conceive of something before it exists, to question what appears inevitable and to imagine oneself otherwise.",
  "Her paintings ask us to recover something of that instinctive freedom. To imagine. To create. And then, as she writes, \u201cto imagine again.\u201d",
  "Through the Looking Glass ultimately occupies the moment before certainty: when the road beneath us is no longer entirely solid and the distinction between the world as it is and the world as it might be begins to dissolve. Grant de Longueuil does not tell the viewer which direction to take. The work offers no instruction and no predetermined destination. It simply reminds us that we are allowed to choose.",
];

export default function ThroughTheLookingGlass() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main
      data-testid="event-page"
      className="bg-nyg-paper text-nyg-dark min-h-screen"
    >
      <Seo
        title="Through the Looking Glass — Rebecca Grant de Longueuil | NYG Projects"
        description="NYG Projects presents Through the Looking Glass, Rebecca Grant de Longueuil's Yellow Brick Road collection. 14 October 2026, 8 PM, Socialista London. RSVP only."
        image="/share/og-through-the-looking-glass.jpg"
        imageAlt="Through the Looking Glass — exhibition poster, Rebecca Grant de Longueuil, 14 October 2026, Socialista London"
        path="/projects/through-the-looking-glass"
        type="article"
      />
      <Nav variant="light" />

      {/* HERO */}
      <section className="relative pt-32 md:pt-40 pb-16 md:pb-24">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16">
          <Link
            to="/projects"
            data-testid="event-back-link"
            className="inline-flex items-center gap-2 label hover:opacity-60 transition-opacity"
          >
            <ArrowLeft size={14} strokeWidth={1} /> Back
          </Link>

          {/* Exhibition poster (left) + title/info (right) */}
          <div className="grid grid-cols-12 gap-8 md:gap-12 lg:gap-16 mt-10 md:mt-14 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
              className="col-span-12 md:col-span-5"
            >
              <div className="overflow-hidden">
                <img
                  src={POSTER}
                  alt="Through the Looking Glass — Exhibition poster"
                  className="w-full h-auto block"
                  data-testid="event-poster"
                />
              </div>
            </motion.div>

            <div className="col-span-12 md:col-span-7">
              <motion.span
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="label block"
              >
                NYG × Socialista London — N° 02
              </motion.span>
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
                data-testid="event-title"
                className="font-heading text-[40px] sm:text-5xl md:text-6xl lg:text-7xl leading-[0.9] tracking-[-0.02em] mt-6"
              >
                Through the
                <br />
                <span className="italic font-light">Looking Glass</span>
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.5 }}
                className="font-body font-light italic text-lg md:text-2xl text-nyg-dark/70 mt-8"
                data-testid="event-artist-line"
              >
                Rebecca Grant de Longueuil
              </motion.p>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.55 }}
                className="label mt-4"
                data-testid="event-collection"
              >
                Collection — Yellow Brick Road
              </motion.p>
            </div>
          </div>
        </div>
      </section>

      {/* DETAILS STRIP */}
      <section className="bg-nyg-dark text-nyg-cream py-16 md:py-20">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 md:grid-cols-4 gap-10 md:gap-6">
          {[
            { icon: <Calendar size={18} strokeWidth={1} />, label: "Date", value: "14 October 2026" },
            { icon: <Clock size={18} strokeWidth={1} />, label: "Time", value: "8:00 PM" },
            { icon: <MapPin size={18} strokeWidth={1} />, label: "Venue", value: "Socialista\nLondon" },
            {
              icon: <Mail size={18} strokeWidth={1} />,
              label: "RSVP",
              value: "RSVP only",
              href: "mailto:info@nygprojects.com?subject=RSVP%20%E2%80%94%20Through%20the%20Looking%20Glass",
              linkText: "info@nygprojects.com",
            },
          ].map((item, i) => (
            <motion.div
              key={item.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.8, delay: i * 0.1 }}
              data-testid={`event-detail-${item.label.toLowerCase()}`}
              className="flex flex-col gap-3"
            >
              <div className="flex items-center gap-3 text-nyg-cream/80">
                {item.icon}
                <span className="label !text-nyg-cream/70">{item.label}</span>
              </div>
              <div className="font-heading text-2xl md:text-[28px] leading-tight whitespace-pre-line">
                {item.value}
                {item.href && (
                  <>
                    {"\n"}
                    <a
                      href={item.href}
                      data-testid="event-rsvp-link"
                      className="underline underline-offset-4 decoration-nyg-cream/40 hover:decoration-nyg-cream transition-colors"
                    >
                      {item.linkText}
                    </a>
                  </>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CURATORIAL STATEMENT */}
      <section className="py-24 md:py-40 bg-nyg-paper">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-12 gap-6 md:gap-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1 }}
            className="col-span-12 md:col-span-4"
          >
            <span className="label">Curatorial Note</span>
            <h2 className="font-heading text-4xl md:text-5xl leading-[0.95] tracking-tight mt-6">
              A path
              <br />
              beyond
              <br />
              <span className="italic font-light">the mirror.</span>
            </h2>
          </motion.div>

          <div className="col-span-12 md:col-span-7 md:col-start-6 space-y-7 md:space-y-9">
            {CURATORIAL.map((p, i) => (
              <motion.p
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.9, delay: i * 0.05 }}
                className="font-body font-light text-lg md:text-xl leading-relaxed text-nyg-dark/85"
                data-testid={`curatorial-paragraph-${i}`}
              >
                {p}
              </motion.p>
            ))}
          </div>
        </div>
      </section>

      {/* ARTIST */}
      <section className="bg-nyg-cream py-24 md:py-40 border-t border-nyg-dark/10">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="flex items-baseline justify-between mb-16 md:mb-24">
            <span className="label">The Artist</span>
            <span className="label hidden sm:inline">Solo Presentation</span>
          </div>
          <div>
            <motion.article
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              data-testid="event-artist-0"
              className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-start"
            >
              <div className="md:col-span-5 overflow-hidden">
                <img
                  src={ARTIST.image}
                  alt={ARTIST.name}
                  className="w-full h-auto block"
                />
              </div>
              <div className="md:col-span-7">
                <h3 className="font-heading italic font-light text-4xl md:text-5xl text-nyg-dark">
                  {ARTIST.name}
                </h3>
                <div className="h-px w-12 bg-nyg-dark my-7" />
                <div className="space-y-5">
                  {ARTIST.paragraphs.map((p, k) => (
                    <p
                      key={k}
                      className="font-body font-light text-base md:text-lg leading-relaxed text-nyg-dark/85"
                    >
                      {p}
                    </p>
                  ))}
                </div>
              </div>
            </motion.article>
          </div>
        </div>
      </section>

      {/* CATALOG */}
      <section
        id="catalog"
        data-testid="catalog-section"
        className="bg-nyg-paper py-24 md:py-40 border-t border-nyg-dark/10"
      >
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1 }}
            className="flex items-baseline justify-between mb-16 md:mb-24"
          >
            <div>
              <span className="label">Art Catalogue</span>
              <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight mt-6">
                Selected
                <br />
                <span className="italic font-light">works.</span>
              </h2>
            </div>
            <span className="label hidden sm:inline">
              {CATALOG.length > 0 ? `${CATALOG.length} works` : "To be announced"}
            </span>
          </motion.div>

          {CATALOG.length === 0 ? (
            <p
              className="font-body font-light italic text-lg md:text-xl text-nyg-dark/60 max-w-xl"
              data-testid="catalog-pending"
            >
              The works from Yellow Brick Road will be revealed here ahead of the
              opening. Request the catalogue below to receive it as soon as it is
              released.
            </p>
          ) : (
            <div className="space-y-24 md:space-y-32">
              {CATALOG.map((w, i) => {
                const reverse = i % 2 === 1;
                return (
                  <motion.article
                    key={w.n}
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                    data-testid={`catalog-item-${i}`}
                    className={`grid grid-cols-12 gap-6 md:gap-12 items-center ${
                      reverse ? "md:[direction:rtl]" : ""
                    }`}
                  >
                    <div className="col-span-12 md:col-span-4 md:[direction:ltr]">
                      <div className="overflow-hidden">
                        <img
                          src={w.image}
                          alt={`${w.title}, ${w.year}`}
                          className="w-full h-auto block"
                        />
                      </div>
                    </div>

                    <div className="col-span-12 md:col-span-7 md:col-start-6 md:[direction:ltr]">
                      <span className="label !text-nyg-dark/50">N° {w.n}</span>
                      <h3 className="font-heading text-4xl md:text-5xl lg:text-[56px] leading-[0.95] tracking-tight mt-5">
                        {w.title}
                        <span className="italic font-light text-nyg-dark/60">
                          , {w.year}
                        </span>
                      </h3>
                      {w.titleAlt && (
                        <p className="font-body italic text-base text-nyg-dark/50 mt-2">
                          {w.titleAlt}
                        </p>
                      )}
                      <div className="h-px w-12 bg-nyg-dark my-7" />
                      <dl className="space-y-3">
                        <div className="flex gap-6">
                          <dt className="label w-28 shrink-0">Artist</dt>
                          <dd className="font-body font-light text-base md:text-lg">
                            {w.artist}
                          </dd>
                        </div>
                        <div className="flex gap-6">
                          <dt className="label w-28 shrink-0">Medium</dt>
                          <dd className="font-body font-light text-base md:text-lg">
                            {w.medium}
                          </dd>
                        </div>
                        <div className="flex gap-6">
                          <dt className="label w-28 shrink-0">Dimensions</dt>
                          <dd className="font-body font-light text-base md:text-lg">
                            {w.dimensions}
                          </dd>
                        </div>
                      </dl>
                      {w.note && (
                        <p className="font-body font-light italic text-base text-nyg-dark/75 leading-relaxed mt-8 max-w-md">
                          {w.note}
                        </p>
                      )}
                    </div>
                  </motion.article>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* REQUEST CATALOGUE */}
      <CatalogueRequest
        project="through-the-looking-glass"
        description="Receive the Yellow Brick Road exhibition catalogue — featuring works by Rebecca Grant de Longueuil, dimensions, mediums and curatorial notes — sent directly to your inbox."
        fileNote="PDF · ~4 MB"
      />

      {/* CURATED BY */}
      <section className="bg-nyg-dark text-nyg-cream py-24 md:py-32">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-12 gap-6 md:gap-10 items-center">
          <div className="col-span-12 md:col-span-4 flex justify-center md:justify-start">
            <Logo size={70} tone="cream" />
          </div>
          <div className="col-span-12 md:col-span-7 md:col-start-6">
            <span className="label !text-nyg-cream/60">Curated by</span>
            <p className="font-heading text-3xl md:text-4xl leading-snug mt-5">
              Nicolette Matakova,
              <br />
              Yasemin Solu <span className="italic font-light">&amp;</span> Gizem Ataman
            </p>
            <p className="font-body font-light text-base text-nyg-cream/70 mt-8 max-w-lg">
              Presented in collaboration with Socialista London. An intimate evening
              — RSVP only — written enquiries welcome at{" "}
              <a
                href="mailto:info@nygprojects.com"
                className="underline underline-offset-4 hover:text-nyg-cream"
              >
                info@nygprojects.com
              </a>
              .
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
